import useInView from '@propertypal/shared/src/hooks/useInView';
import noPhoto from '@propertypal/shared/src/resources/fallbacks/no-photo-icon.png';
import { trackGaEvent } from '@propertypal/shared/src/services/analytics';
import { Property } from '@propertypal/shared/src/types/property';
import { getMainImage } from '@propertypal/shared/src/utils/property';
import React, { FunctionComponent, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import LazyImage from '../image/LazyImage';
import PropertyPrice from '../property/PropertyPrice';
import { SubHeading, Text } from '../typography';
import {
  CarouselRoot,
  Curtain,
  LinkBox,
  SmallPropertyBody,
  SmallPropertyCard,
} from './PinnedDevelopmentPropertyCarousel.style';

interface Props {
  properties: Property[];
  imageLocator?: string;
  loading?: 'lazy' | 'eager';
}

const PinnedDevelopmentPropertyCarousel: FunctionComponent<Props> = ({ properties, imageLocator, loading }) => {
  const theme = useTheme();
  const [beginning, setBeginning] = useState<boolean>(true);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useInView(containerRef, { threshold: 0.5 }, () => {
    trackGaEvent('pinned_development_impression', {});
  });

  return (
    <CarouselRoot ref={containerRef}>
      {beginning && <Curtain />}

      <Swiper
        slidesPerView="auto"
        spaceBetween={10}
        watchOverflow
        modules={[Navigation]}
        navigation
        onSlideChange={(s) => {
          setBeginning(s.isBeginning);
        }}
      >
        {properties.map((subProperty, index) => {
          const [image] = getMainImage(subProperty, imageLocator);

          return (
            <SwiperSlide key={subProperty.id} style={{ width: 250, height: 'auto' }}>
              <SmallPropertyCard href={subProperty.path}>
                <LazyImage
                  src={image || noPhoto.src}
                  width={340}
                  height={255}
                  loading={index === 0 ? loading : 'lazy'}
                  alt={`${subProperty.displayAddress} photo`}
                  containerStyle={{ minHeight: 187.5, maxHeight: 187.5 }}
                />

                <SmallPropertyBody>
                  <PropertyPrice
                    containerStyle={{ marginBottom: 0, fontWeight: 600 }}
                    price={subProperty.price}
                    alternativeCurrencyPrice={subProperty.alternativeCurrencyPrice}
                    status={subProperty.status}
                    comingSoonText={subProperty.comingSoonText}
                    fontSize={24}
                    prefixBreak
                  />

                  <SubHeading fontSize={16} color={theme.textDark} fontWeight="600">
                    {subProperty.addressLine1}
                  </SubHeading>

                  {!!subProperty.briefText && (
                    <Text fontSize={14} color={theme.textLight}>
                      {subProperty.briefText}
                    </Text>
                  )}
                </SmallPropertyBody>
              </SmallPropertyCard>
            </SwiperSlide>
          );
        })}

        <SwiperSlide style={{ width: 250, height: 'auto' }}>
          <LinkBox>
            <h3>Find out more about this development</h3>
            <p>View development</p>
          </LinkBox>
        </SwiperSlide>
      </Swiper>
    </CarouselRoot>
  );
};

export default PinnedDevelopmentPropertyCarousel;
